<template>
  <ion-modal :is-open="isOpen" @didDismiss="$emit('close')" class="candidates-browser">
    <ion-header>
      <ion-toolbar>
        <ion-title>Curate Candidates</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')">Close</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div v-if="loading" class="loading-container">
        <ion-spinner></ion-spinner>
        <p>Loading candidates...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <p>{{ error }}</p>
        <ion-button @click="loadData">Retry</ion-button>
      </div>

      <div v-else-if="candidates.length === 0" class="empty-container">
        <p>No candidates found.</p>
        <p><small>Run <code>yarn run compile:candidates</code> to compile candidate levels.</small></p>
      </div>

      <template v-else>
        <div class="controls">
          <div class="filter-row">
            <ion-segment v-model="filterStatus" @ionChange="applyFilters">
              <ion-segment-button value="all">
                <ion-label>All ({{ counts.all }})</ion-label>
              </ion-segment-button>
              <ion-segment-button value="uncurated">
                <ion-label>Uncurated ({{ counts.uncurated }})</ion-label>
              </ion-segment-button>
              <ion-segment-button value="keep">
                <ion-label>Keep ({{ counts.keep }})</ion-label>
              </ion-segment-button>
              <ion-segment-button value="needs_tweaks">
                <ion-label>Tweaks ({{ counts.needs_tweaks }})</ion-label>
              </ion-segment-button>
            </ion-segment>
          </div>
          <div class="sort-row">
            <ion-select v-model="sortBy" interface="popover" label="Sort by" @ionChange="applyFilters">
              <ion-select-option value="date">Date</ion-select-option>
              <ion-select-option value="turns">Turns to Win</ion-select-option>
              <ion-select-option value="brittleness">Brittleness</ion-select-option>
              <ion-select-option value="paths">Winning Paths</ion-select-option>
              <ion-select-option value="difficulty">Difficulty Rating</ion-select-option>
            </ion-select>
            <ion-checkbox v-model="showTweaksOnly" @ionChange="applyFilters">Tweaks folder only</ion-checkbox>
          </div>
        </div>

        <ion-list>
          <ion-item
            v-for="candidate in filteredCandidates"
            :key="candidate.id"
            button
            @click="selectCandidate(candidate)"
            :class="{ 'tweaks-item': candidate.folder === 'tweaks' }"
          >
            <ion-label>
              <h2>
                {{ candidate.id }}
                <ion-badge v-if="candidate.folder === 'tweaks'" color="warning" class="folder-badge">tweaks</ion-badge>
              </h2>
              <p>
                Turns: {{ candidate.metrics.turnsToWin }} |
                Brittleness: {{ candidate.metrics.brittlenessScore }} |
                Paths: {{ candidate.metrics.winningPathsCount }}
              </p>
            </ion-label>
            <ion-badge
              slot="end"
              :color="getCurationColor(candidate.curation?.status)"
            >
              {{ getCurationLabel(candidate.curation) }}
            </ion-badge>
          </ion-item>
        </ion-list>
      </template>
    </ion-content>
  </ion-modal>
</template>

<script setup>
import { inject, ref, computed, onMounted, watch } from 'vue';
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
  IonContent, IonList, IonItem, IonLabel, IonBadge, IonSpinner,
  IonSegment, IonSegmentButton, IonSelect, IonSelectOption, IonCheckbox,
} from '@ionic/vue';
import { GameEvents } from '../../../lib/GameEvents';

const DEV_SERVER_URL = 'http://localhost:3001';

const props = defineProps({
  isOpen: Boolean,
});

const emit = defineEmits(['close']);
const game = inject('game');

const loading = ref(true);
const error = ref(null);
const candidates = ref([]);
const curationData = ref({});
const filterStatus = ref('all');
const sortBy = ref('date');
const showTweaksOnly = ref(false);

const counts = computed(() => {
  const all = candidates.value.length;
  let uncurated = 0, keep = 0, needs_tweaks = 0;
  for (const c of candidates.value) {
    const status = c.curation?.status;
    if (!status) uncurated++;
    else if (status === 'keep') keep++;
    else if (status === 'needs_tweaks') needs_tweaks++;
  }
  return { all, uncurated, keep, needs_tweaks };
});

const filteredCandidates = computed(() => {
  let result = [...candidates.value];

  // Apply folder filter
  if (showTweaksOnly.value) {
    result = result.filter(c => c.folder === 'tweaks');
  }

  // Apply status filter
  if (filterStatus.value === 'uncurated') {
    result = result.filter(c => !c.curation?.status);
  } else if (filterStatus.value === 'keep') {
    result = result.filter(c => c.curation?.status === 'keep');
  } else if (filterStatus.value === 'needs_tweaks') {
    result = result.filter(c => c.curation?.status === 'needs_tweaks');
  }
  // 'all' shows everything (including rejected)

  // Apply sorting
  result.sort((a, b) => {
    switch (sortBy.value) {
      case 'turns':
        return a.metrics.turnsToWin - b.metrics.turnsToWin;
      case 'brittleness':
        return a.metrics.brittlenessScore - b.metrics.brittlenessScore;
      case 'paths':
        return b.metrics.winningPathsCount - a.metrics.winningPathsCount;
      case 'difficulty':
        return (a.curation?.difficulty || 99) - (b.curation?.difficulty || 99);
      case 'date':
      default:
        return new Date(b.generatedAt) - new Date(a.generatedAt);
    }
  });

  return result;
});

const getCurationColor = (status) => {
  switch (status) {
    case 'keep': return 'success';
    case 'needs_tweaks': return 'warning';
    case 'rejected': return 'danger';
    default: return 'medium';
  }
};

const getCurationLabel = (curation) => {
  if (!curation?.status) return 'Uncurated';
  const diffLabel = curation.difficulty ? ` (${curation.difficulty})` : '';
  switch (curation.status) {
    case 'keep': return `Keep${diffLabel}`;
    case 'needs_tweaks': return `Tweaks${diffLabel}`;
    case 'rejected': return 'Rejected';
    default: return curation.status;
  }
};

const loadData = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Load manifest via dynamic import
    const manifestModule = await import('../../../../content/dist/candidates/manifest.json');
    const manifest = manifestModule.default || manifestModule;

    // Load curation data from dev server
    try {
      const curationResponse = await fetch(`${DEV_SERVER_URL}/api/curation`);
      if (curationResponse.ok) {
        curationData.value = await curationResponse.json();
      }
    } catch (e) {
      console.warn('Dev server not available, curation data not loaded');
    }

    // Merge curation data into candidates
    candidates.value = manifest.candidates.map(c => ({
      ...c,
      curation: curationData.value[c.id] || null,
    }));

  } catch (e) {
    console.error('Failed to load candidates:', e);
    error.value = 'Manifest not found. Run yarn run compile:candidates first.';
  } finally {
    loading.value = false;
  }
};

const applyFilters = () => {
  // Filters are reactive, this just ensures UI updates
};

const selectCandidate = (candidate) => {
  emit('close');
  game.emit(GameEvents.PLAY_CANDIDATE, {
    candidateId: candidate.id,
    existingCuration: candidate.curation,
  });
};

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    loadData();
  }
});

onMounted(() => {
  if (props.isOpen) {
    loadData();
  }
});
</script>

<style scoped>
.candidates-browser {
  --width: 90%;
  --max-width: 800px;
  --height: 90%;
}

.loading-container,
.error-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #888;
}

.controls {
  padding: 12px;
  background: var(--ion-background-color);
  border-bottom: 1px solid var(--ion-border-color);
}

.filter-row {
  margin-bottom: 8px;
}

.sort-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.sort-row ion-select {
  max-width: 200px;
}

.tweaks-item {
  --background: rgba(255, 196, 0, 0.1);
}

.folder-badge {
  margin-left: 8px;
  font-size: 0.7em;
  vertical-align: middle;
}

ion-item h2 {
  font-size: 0.95em;
}

ion-item p {
  font-size: 0.8em;
  color: #888;
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}
</style>
